import { LegalLayout, LegalSection } from "../components/LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      updated="June 6, 2026"
      path="/privacy-policy"
      description="How Travel Club Navigator Group collects, uses, and protects your personal information."
    >
      <p data-testid="privacy-intro">
        Travel Club Navigator Group ("we," "us," or "our") respects your privacy and is committed to
        protecting the personal information you share with us. This Privacy Policy explains what we
        collect, how we use it, and the choices you have. By using{" "}
        <a href="https://travelclubnavigator.co" className="text-primary hover:underline">
          travelclubnavigator.co
        </a>{" "}
        (the "Site"), you agree to the practices described here.
      </p>

      <LegalSection heading="1. Information We Collect">
        <p>We collect information you provide directly and information gathered automatically:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li><strong>Contact &amp; inquiry data:</strong> name, email, phone number, and message content when you submit an inquiry, contact form, or partner request.</li>
          <li><strong>Booking data:</strong> travel dates, room selection, number of travelers, and billing name/email when you make a reservation.</li>
          <li><strong>Payment data:</strong> card payments are processed securely by Stripe. We do <strong>not</strong> store or have access to your full card number.</li>
          <li><strong>Usage data:</strong> IP address, browser type, pages visited, and similar analytics collected via cookies and similar technologies.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="2. How We Use Your Information">
        <ul className="list-disc space-y-2 pl-6">
          <li>To respond to inquiries and provide the reviews, guides, and services you request.</li>
          <li>To process and confirm vacation package bookings and payments.</li>
          <li>To send booking confirmations and, where you have opted in, occasional updates.</li>
          <li>To operate, maintain, secure, and improve the Site.</li>
          <li>To comply with legal obligations and prevent fraud or abuse.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. Cookies &amp; Tracking">
        <p>
          We use cookies and similar technologies to keep the Site functioning, remember preferences,
          and understand how the Site is used. You can control cookies through your browser settings;
          disabling some cookies may affect Site functionality.
        </p>
      </LegalSection>

      <LegalSection heading="4. How We Share Information">
        <p>
          We do not sell your personal information. We share it only with service providers who help us
          operate the Site and fulfill bookings, including:
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li><strong>Stripe</strong> — payment processing.</li>
          <li><strong>Email delivery providers</strong> — sending confirmations and notifications.</li>
          <li><strong>Travel partners</strong> — only as needed to fulfill a reservation you request.</li>
          <li><strong>Legal authorities</strong> — when required by law or to protect our rights.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="5. Your Rights &amp; Choices">
        <p>
          Depending on your location (including under the GDPR and California's CCPA/CPRA), you may have
          the right to access, correct, delete, or restrict the use of your personal information, and to
          opt out of certain processing. To exercise these rights, contact us at{" "}
          <a href="mailto:hello@travelclubnavigator.co" className="text-primary hover:underline">
            hello@travelclubnavigator.co
          </a>. We will respond within the time required by applicable law.
        </p>
      </LegalSection>

      <LegalSection heading="6. Data Security &amp; Retention">
        <p>
          We use reasonable administrative and technical safeguards to protect your information. No method
          of transmission or storage is 100% secure. We retain personal information only as long as
          necessary for the purposes described above or as required by law.
        </p>
      </LegalSection>

      <LegalSection heading="7. Children's Privacy">
        <p>The Site is not directed to children under 16, and we do not knowingly collect their information.</p>
      </LegalSection>

      <LegalSection heading="8. Changes to This Policy">
        <p>
          We may update this Privacy Policy periodically. Material changes will be reflected by the "Last
          updated" date above. Continued use of the Site constitutes acceptance of the revised policy.
        </p>
      </LegalSection>

      <LegalSection heading="9. Contact Us">
        <p>
          Questions about this policy? Email{" "}
          <a href="mailto:hello@travelclubnavigator.co" className="text-primary hover:underline">
            hello@travelclubnavigator.co
          </a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
